import { CardHeader } from "@bmi-digital/components/card";
import CompanyDetails from "@bmi-digital/components/company-details";
import GoogleMap from "@bmi-digital/components/google-map";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import classnames from "classnames";
import React, { useState } from "react";
import CloseIconButton from "@bmi-digital/components/close-icon-button";
import { useSiteContext } from "../../Site";
import { calculateCentre } from "../helpers";
import {
  CompanyLogo,
  ProductDetailsCard,
  ProductDetailsCardBody,
  StyledServiceLocatorMap,
  companyLogoClasses,
  productDetailsCardClasses
} from "../styles/styles";
import { MapInfoOverlay } from "./MapInfoOverlay";
import type {
  LatLngLiteral as GoogleLatLngLiteral,
  MarkerOptionsWithData
} from "@bmi-digital/components/google-api";
import type { CompanyDetailProps } from "@bmi-digital/components/company-details";
import type { ServiceData } from "../../Service";

export interface MapProps {
  initialMapCentre: [number, number] | null;
  markers: MarkerOptionsWithData<ServiceData>[];
  zoom: number;
  selectedRoofer: ServiceData | null;
  handleMarkerClick: (service: ServiceData) => void;
  clearRooferAndResetMap: () => void;
  handleCloseCard: () => void;
  centre: GoogleLatLngLiteral;
  getCompanyDetails: (service: ServiceData) => CompanyDetailProps;
  onDismissOverlay: () => void;
  overlay?: {
    title: string;
    description?: string | null;
  } | null;
}

export const ServiceLocatorMap = ({
  initialMapCentre,
  markers,
  zoom,
  selectedRoofer,
  handleMarkerClick,
  clearRooferAndResetMap,
  handleCloseCard,
  centre,
  getCompanyDetails,
  onDismissOverlay,
  overlay
}: MapProps): React.ReactElement => {
  const { getMicroCopy } = useSiteContext();
  const [newCentre, setNewCentre] = useState<GoogleLatLngLiteral>();
  const closeButtonLabel = getMicroCopy(microCopy.GLOBAL_CLOSE);

  return (
    <StyledServiceLocatorMap>
      <GoogleMap
        center={calculateCentre(newCentre ?? centre, initialMapCentre)}
        markers={markers}
        onMarkerClick={handleMarkerClick}
        onClusterClick={() => {
          handleCloseCard();
          onDismissOverlay();
        }}
        zoom={zoom}
        selectedMarkerName={
          selectedRoofer
            ? `${selectedRoofer.name}-${selectedRoofer.id}`
            : undefined
        }
        passNewCenter={(c: google.maps.LatLng) => setNewCentre(c.toJSON())}
        handleCloseCard={handleCloseCard}
      >
        {selectedRoofer && (
          <ProductDetailsCard
            className={classnames(
              selectedRoofer.companyLogo && productDetailsCardClasses.withLogo
            )}
            data-testid={`service-locator-service-details-card-${selectedRoofer.id}`}
          >
            <CardHeader
              avatar={
                selectedRoofer.companyLogo && (
                  <CompanyLogo
                    className={companyLogoClasses.card}
                    data-testid={`service-locator-service-details-card-logo-${selectedRoofer.id}`}
                    {...selectedRoofer.companyLogo}
                  />
                )
              }
              title={selectedRoofer.name}
              action={
                <CloseIconButton
                  accessibilityLabel={closeButtonLabel}
                  onClick={clearRooferAndResetMap}
                  gtm={{
                    id: "cta-click1",
                    label: closeButtonLabel
                  }}
                />
              }
            />
            <ProductDetailsCardBody>
              <CompanyDetails {...getCompanyDetails(selectedRoofer)} />
            </ProductDetailsCardBody>
          </ProductDetailsCard>
        )}
      </GoogleMap>
      {overlay?.title && (
        <MapInfoOverlay
          onClose={() => onDismissOverlay()}
          title={overlay.title}
          description={overlay.description}
        />
      )}
    </StyledServiceLocatorMap>
  );
};
